'use client';

import { useEffect, useState } from 'react';

import type { HeadingRecord } from '@/content/schemas';
import { cn } from '@/lib/utils';

type TableOfContentsProps = {
  headings: HeadingRecord[];
};

export function TableOfContents({ headings }: TableOfContentsProps) {
  const levelTwo = headings.filter((heading) => heading.level === 2);
  const [activeId, setActiveId] = useState<string>('');

  useEffect(() => {
    if (levelTwo.length === 0) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((entry) => entry.isIntersecting)
          .sort(
            (left, right) =>
              left.boundingClientRect.top - right.boundingClientRect.top
          );
        if (visible.length > 0) {
          setActiveId(visible[0].target.id);
        }
      },
      {
        rootMargin: '-80px 0% -70% 0%',
        threshold: 0,
      }
    );

    for (const heading of levelTwo) {
      const element = document.getElementById(heading.id);
      if (element) observer.observe(element);
    }

    return () => observer.disconnect();
  }, [levelTwo]);

  if (levelTwo.length === 0) return null;

  return (
    <aside className="hidden lg:block">
      <nav aria-label="Table of contents" className="sticky top-28">
        <p className="text-muted-foreground mb-4 font-mono text-[10px] uppercase tracking-widest">
          On this page
        </p>
        <ul className="border-border space-y-2 border-l pl-4">
          {levelTwo.map((heading) => (
            <li key={heading.id}>
              <a
                href={`#${heading.id}`}
                className={cn(
                  'block font-mono text-xs leading-relaxed transition-colors',
                  activeId === heading.id
                    ? 'text-brand font-semibold'
                    : 'text-muted-foreground hover:text-ink'
                )}
              >
                {heading.text}
              </a>
            </li>
          ))}
        </ul>
      </nav>
    </aside>
  );
}
